import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import { useAuth } from '../context/AuthContext';
import './Settings.css';
import './AdminSettings.css';

const AdminSettings = () => {
  const navigate = useNavigate();
  const { user: currentUser, login } = useAuth();
  const [settings, setSettings] = useState({
    supabase: { url: '', serviceRoleKey: '' },
    app: {
      mode: 'live',
      adminEmail: 'admin@ploko.nl',
      adminPassword: ''
    },
    smtp: {
      host: '',
      port: 587,
      secure: false,
      user: '',
      pass: '',
      from: ''
    }
  });
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', password: '', name: '', role: 'subscriber' });
  const [passwordEditId, setPasswordEditId] = useState(null);
  const [passwordEditValue, setPasswordEditValue] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [licenseGenerating, setLicenseGenerating] = useState(null); // userId of the user being generated
  const [werkUrlEditId, setWerkUrlEditId] = useState(null);
  const [werkUrlEditValue, setWerkUrlEditValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [demoStats, setDemoStats] = useState([]);
  const [demoStatsLoading, setDemoStatsLoading] = useState(false);
  const [demoAccess, setDemoAccess] = useState([]);
  const [demoAccessLoading, setDemoAccessLoading] = useState(false);

  useEffect(() => {
    loadSettings();
    loadUsers();
    loadDemoStats();
    loadDemoAccess();
  }, []);

  const loadDemoStats = async () => {
    setDemoStatsLoading(true);
    try {
      const res = await api.get('/api/admin/demo-stats?days=30');
      setDemoStats(Array.isArray(res.data) ? res.data : []);
    } catch (_) {
      setDemoStats([]);
    } finally {
      setDemoStatsLoading(false);
    }
  };

  const loadDemoAccess = async () => {
    setDemoAccessLoading(true);
    try {
      const res = await api.get('/api/admin/demo-access');
      setDemoAccess(Array.isArray(res.data) ? res.data : []);
    } catch (_) {
      setDemoAccess([]);
    } finally {
      setDemoAccessLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await api.get('/api/settings');
      const data = response.data || {};
      setSettings({
        supabase: {
          url: data.supabase?.url ?? '',
          serviceRoleKey: data.supabase?.serviceRoleKey ?? ''
        },
        app: {
          mode: data.app?.mode ?? 'live',
          adminEmail: data.app?.adminEmail ?? 'admin@ploko.nl',
          adminPassword: data.app?.adminPassword === '***' ? '' : (data.app?.adminPassword ?? ''),
          registrationDisabled: !!data.app?.registrationDisabled,
          videoUrl1: data.app?.videoUrl1 ?? '',
          videoUrl2: data.app?.videoUrl2 ?? ''
        },
        smtp: {
          host: data.smtp?.host ?? '',
          port: data.smtp?.port ?? 587,
          secure: data.smtp?.secure ?? false,
          user: data.smtp?.user ?? '',
          pass: data.smtp?.pass === '***' ? '' : (data.smtp?.pass ?? ''),
          from: data.smtp?.from ?? ''
        }
      });
    } catch (err) {
      setError('Instellingen laden mislukt.');
      console.error(err);
    }
  };

  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      const res = await api.get('/api/admin/users');
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Users load error:', err);
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  const handleChange = (section, field, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: { ...prev[section], [field]: value }
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const full = await api.get('/api/settings').then(r => r.data);
      const appUpdate = {
        ...full.app,
        mode: settings.app?.mode,
        adminEmail: settings.app?.adminEmail,
        registrationDisabled: !!settings.app?.registrationDisabled,
        videoUrl1: settings.app?.videoUrl1 || '',
        videoUrl2: settings.app?.videoUrl2 || ''
      };
      if (settings.app?.adminPassword && settings.app.adminPassword !== '') {
        appUpdate.adminPassword = settings.app.adminPassword;
      }
      const smtpUpdate = { ...(full.smtp || {}), ...settings.smtp };
      if (!settings.smtp?.pass && full.smtp?.pass) smtpUpdate.pass = full.smtp.pass;
      await api.put('/api/settings', {
        ...full,
        supabase: settings.supabase,
        app: appUpdate,
        smtp: smtpUpdate
      });
      setSuccess('Instellingen opgeslagen.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Opslaan mislukt');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAdmin = async (user) => {
    if (user.is_fallback_admin) return;
    const newRole = user.role === 'admin' ? 'subscriber' : 'admin';
    try {
      await api.put(`/api/admin/users/${user.id}`, { role: newRole });
      setSuccess(newRole === 'admin' ? 'Gebruiker is nu beheerder.' : 'Beheerderrechten verwijderd.');
      loadUsers();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Bijwerken mislukt');
    }
  };

  const handleToggleActive = async (user) => {
    if (user.is_fallback_admin) return;
    const newActive = !user.is_active;
    try {
      await api.put(`/api/admin/users/${user.id}`, { is_active: newActive });
      setSuccess(newActive ? 'Gebruiker is weer actief.' : 'Gebruiker uitgeschakeld.');
      loadUsers();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Bijwerken mislukt');
    }
  };

  const handleSaveWerkUrl = async (userId) => {
    try {
      await api.put(`/api/admin/users/${userId}`, { werk_url: werkUrlEditValue || null });
      setSuccess('Werk-URL opgeslagen.');
      setWerkUrlEditId(null);
      setWerkUrlEditValue('');
      loadUsers();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Werk-URL opslaan mislukt');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newUser.email || !newUser.password) {
      setError('E-mail en wachtwoord zijn verplicht.');
      return;
    }
    setError(null);
    try {
      await api.post('/api/admin/users', {
        email: newUser.email.trim(),
        password: newUser.password,
        name: newUser.name.trim() || undefined,
        role: newUser.role
      });
      setSuccess('Gebruiker aangemaakt.');
      setNewUser({ email: '', password: '', name: '', role: 'subscriber' });
      loadUsers();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Aanmaken mislukt');
    }
  };

  const handleLoginAs = async (u) => {
    if (u.is_fallback_admin) return;
    try {
      const adminToken = localStorage.getItem('authToken');
      if (adminToken) sessionStorage.setItem('adminTokenBeforeImpersonate', adminToken);
      const res = await api.post(`/api/admin/login-as/${u.id}`);
      if (res.data?.success && res.data?.token) {
        if (res.data.impersonating) sessionStorage.setItem('impersonating', '1');
        login({ token: res.data.token, user: res.data.user });
        navigate('/');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Inloggen als deze gebruiker mislukt');
    }
  };

  const handleSetPassword = async (userId) => {
    if (!passwordEditValue || passwordEditValue.length < 6) {
      setError('Wachtwoord moet minimaal 6 tekens zijn.');
      return;
    }
    setError(null);
    setPasswordSaving(true);
    try {
      await api.put(`/api/admin/users/${userId}/password`, { password: passwordEditValue });
      setSuccess('Wachtwoord bijgewerkt.');
      setPasswordEditId(null);
      setPasswordEditValue('');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Wachtwoord wijzigen mislukt');
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleGenerateLicense = async (userId) => {
    setLicenseGenerating(userId);
    setError(null);
    try {
      const res = await api.post(`/api/admin/users/${userId}/generate-license`);
      if (res.data?.license_key) {
        setSuccess(`Licentiecode gegenereerd: ${res.data.license_key}`);
        loadUsers();
        setTimeout(() => setSuccess(null), 8000);
      } else {
        setError('Geen licentiecode ontvangen van de server.');
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Licentiecode genereren mislukt';
      setError(msg);
      console.error('Generate license error:', err);
    } finally {
      setLicenseGenerating(null);
    }
  };

  const getLicenseStatus = (u) => {
    if (u.role === 'admin') return { label: 'Admin', color: '#6366f1' };
    if (u.license_active) return { label: 'Actief', color: '#16a34a' };
    const trialEnd = u.trial_ends_at ? new Date(u.trial_ends_at) : null;
    if (trialEnd && trialEnd > new Date()) {
      const days = Math.ceil((trialEnd - new Date()) / (1000 * 60 * 60 * 24));
      return { label: `Trial (${days}d)`, color: '#d97706' };
    }
    return { label: 'Verlopen', color: '#dc2626' };
  };

  return (
    <div className="settings-page admin-settings-page">
      <div className="settings-page-header">
        <h2>Admin instellingen</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="btn btn-secondary btn-small" onClick={() => navigate('/instellingen/admin/cms')}>
            Content (CMS)
          </button>
          <button type="button" className="btn btn-secondary btn-small" onClick={() => navigate('/')}>
            ← Terug naar overzicht
          </button>
        </div>
      </div>
      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      <div className="settings-section demo-stats-section">
        <h3>Demo-statistieken</h3>
        <p className="settings-help" style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
          Aantal keer dat de demo is geopend en afgerond per dag (laatste 30 dagen).
        </p>
        {demoStatsLoading ? (
          <p style={{ color: '#6b7280' }}>Laden...</p>
        ) : demoStats.length === 0 ? (
          <p style={{ color: '#6b7280' }}>Nog geen demo-statistieken.</p>
        ) : (
          <div className="demo-stats-chart">
            <div className="demo-stats-legend">
              <span className="demo-stats-legend-item"><span className="demo-stats-bar opened" /> Demo geopend</span>
              <span className="demo-stats-legend-item"><span className="demo-stats-bar completed" /> Demo afgerond</span>
            </div>
            <div className="demo-stats-bars">
              {(() => {
                const maxOpened = Math.max(1, ...demoStats.map(d => d.opened));
                const maxCompleted = Math.max(1, ...demoStats.map(d => d.completed));
                return demoStats.slice().reverse().map((day) => (
                  <div key={day.date} className="demo-stats-row">
                    <span className="demo-stats-date">{day.date}</span>
                    <div className="demo-stats-bar-group">
                      <div className="demo-stats-bar opened" style={{ flex: Math.max(0.02, day.opened / maxOpened) }} title={`Geopend: ${day.opened}`} />
                      <div className="demo-stats-bar completed" style={{ flex: Math.max(0.02, day.completed / maxCompleted) }} title={`Afgerond: ${day.completed}`} />
                    </div>
                    <span className="demo-stats-values">{day.opened} / {day.completed}</span>
                  </div>
                ));
              })()}
            </div>
          </div>
        )}
      </div>

      <div className="settings-section demo-access-section">
        <h3>Demo-toegangen</h3>
        <p className="settings-help" style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
          E-mailadressen die de demo hebben geopend (nieuwste bovenaan).
        </p>
        {demoAccessLoading ? (
          <p style={{ color: '#6b7280' }}>Laden...</p>
        ) : demoAccess.length === 0 ? (
          <p style={{ color: '#6b7280' }}>Nog geen demo-toegangen geregistreerd.</p>
        ) : (
          <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
            <table className="demo-access-table">
              <thead>
                <tr>
                  <th>Datum</th>
                  <th>E-mailadres</th>
                </tr>
              </thead>
              <tbody>
                {demoAccess.map((entry, i) => (
                  <tr key={i}>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {new Date(entry.date).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      {' '}
                      <span style={{ color: '#9ca3af', fontSize: 12 }}>
                        {new Date(entry.date).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </td>
                    <td>{entry.email}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {demoAccess.length > 0 && (
          <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 8 }}>{demoAccess.length} registratie(s) totaal</p>
        )}
      </div>

      <form onSubmit={handleSave}>
        <div className="settings-section">
          <h3>Supabase Database</h3>
          <p className="settings-help" style={{ fontSize: '12px', color: '#6b7280', marginBottom: '16px' }}>
            Verbind met je Supabase project voor multi-tenant functionaliteit.
            <br />
            <a href="https://supabase.com" target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6' }}>Maak een Supabase project aan</a> en importeer het schema uit <code>database/schema.sql</code>
          </p>
          <div className="input-group">
            <label>Supabase URL</label>
            <input
              type="text"
              value={settings.supabase?.url || ''}
              onChange={(e) => handleChange('supabase', 'url', e.target.value)}
              placeholder="https://xxxxx.supabase.co"
            />
          </div>
          <div className="input-group">
            <label>Service Role Key</label>
            <input
              type="password"
              value={settings.supabase?.serviceRoleKey || ''}
              onChange={(e) => handleChange('supabase', 'serviceRoleKey', e.target.value)}
              placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
            />
            <small style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px', display: 'block' }}>
              Vind deze in: Settings → API → service_role key
            </small>
          </div>
        </div>

        <div className="settings-section">
          <h3>SMTP-server (wachtwoord e-mail)</h3>
          <p className="settings-help" style={{ fontSize: '12px', color: '#6b7280', marginBottom: '16px' }}>
            Gebruikt voor &quot;Wachtwoord vergeten&quot;: een tijdelijk wachtwoord wordt per e-mail verstuurd.
          </p>
          <div className="input-group">
            <label>SMTP-host</label>
            <input
              type="text"
              value={settings.smtp?.host || ''}
              onChange={(e) => handleChange('smtp', 'host', e.target.value)}
              placeholder="smtp.voorbeeld.nl"
            />
          </div>
          <div className="input-group">
            <label>Poort</label>
            <input
              type="number"
              value={settings.smtp?.port ?? 587}
              onChange={(e) => handleChange('smtp', 'port', parseInt(e.target.value, 10) || 587)}
              placeholder="587"
            />
          </div>
          <div className="input-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={settings.smtp?.secure === true}
                onChange={(e) => handleChange('smtp', 'secure', e.target.checked)}
              />
              <span>Beveiligde verbinding (TLS)</span>
            </label>
          </div>
          <div className="input-group">
            <label>Gebruikersnaam</label>
            <input
              type="text"
              value={settings.smtp?.user || ''}
              onChange={(e) => handleChange('smtp', 'user', e.target.value)}
              placeholder="smtp-gebruiker"
            />
          </div>
          <div className="input-group">
            <label>Wachtwoord</label>
            <input
              type="password"
              value={settings.smtp?.pass || ''}
              onChange={(e) => handleChange('smtp', 'pass', e.target.value)}
              placeholder="Laat leeg om niet te wijzigen"
            />
          </div>
          <div className="input-group">
            <label>Afzenderadres (From)</label>
            <input
              type="email"
              value={settings.smtp?.from || ''}
              onChange={(e) => handleChange('smtp', 'from', e.target.value)}
              placeholder="noreply@ploko.nl"
            />
          </div>
        </div>

        <div className="settings-section">
          <h3>Applicatie Instellingen</h3>
          <div className="input-group">
            <label>Modus</label>
            <select
              value={settings.app?.mode || 'live'}
              onChange={(e) => handleChange('app', 'mode', e.target.value)}
            >
              <option value="live">Live (met beveiliging)</option>
              <option value="construction">Under Construction (zonder beveiliging)</option>
            </select>
          </div>
          <div className="input-group">
            <label>Admin E-mail (fallback login)</label>
            <input
              type="email"
              value={settings.app?.adminEmail || ''}
              onChange={(e) => handleChange('app', 'adminEmail', e.target.value)}
              placeholder="admin@ploko.nl"
            />
          </div>
          <div className="input-group">
            <label>Admin Wachtwoord (fallback)</label>
            <input
              type="password"
              value={settings.app?.adminPassword || ''}
              onChange={(e) => handleChange('app', 'adminPassword', e.target.value)}
              placeholder="Nieuw wachtwoord (laat leeg om niet te wijzigen)"
            />
          </div>
          <div className="input-group" style={{ marginTop: 16, padding: 14, background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8 }}>
            <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input
                type="checkbox"
                checked={!!settings.app?.registrationDisabled}
                onChange={(e) => handleChange('app', 'registrationDisabled', e.target.checked)}
              />
              <span style={{ fontWeight: 600, color: '#991b1b' }}>Voorkom nieuwe registraties</span>
            </label>
            <p style={{ fontSize: 12, color: '#6b7280', margin: '6px 0 0 0' }}>
              Als dit aan staat kunnen nieuwe gebruikers zich niet registreren. Op het inlogscherm verschijnt een melding.
            </p>
          </div>
          <div className="input-group" style={{ marginTop: 16 }}>
            <label>Video URL 1 (landingspagina "Hoe het werkt")</label>
            <input
              type="url"
              value={settings.app?.videoUrl1 || ''}
              onChange={(e) => handleChange('app', 'videoUrl1', e.target.value)}
              placeholder="https://www.youtube.com/embed/..."
            />
            <small style={{ color: '#6b7280', marginTop: 4, display: 'block' }}>YouTube embed-URL. Wordt getoond in het "Hoe het werkt"-blok op de landingspagina.</small>
          </div>
          <div className="input-group">
            <label>Video URL 2 (landingspagina "Hoe het werkt")</label>
            <input
              type="url"
              value={settings.app?.videoUrl2 || ''}
              onChange={(e) => handleChange('app', 'videoUrl2', e.target.value)}
              placeholder="https://www.youtube.com/embed/..."
            />
          </div>
        </div>
        <div className="settings-actions">
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/')}>
            Terug
          </button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Opslaan...' : 'Instellingen opslaan'}
          </button>
        </div>
      </form>

      <div className="admin-users-section">
        <h3>Gebruikersbeheer</h3>
        <p className="settings-help" style={{ fontSize: '12px', color: '#6b7280', marginBottom: '16px' }}>
          Beheerders zien het adminmenu en kunnen gebruikers aanmaken of uitzetten. Schakel een gebruiker uit om inloggen te blokkeren.
        </p>

        <form onSubmit={handleCreateUser} className="admin-new-user-form">
          <h4>Nieuwe gebruiker aanmaken</h4>
          <div className="admin-new-user-row">
            <input
              type="email"
              placeholder="E-mail *"
              value={newUser.email}
              onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
              required
            />
            <input
              type="password"
              placeholder="Wachtwoord *"
              value={newUser.password}
              onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
              required
            />
            <input
              type="text"
              placeholder="Naam (optioneel)"
              value={newUser.name}
              onChange={(e) => setNewUser(prev => ({ ...prev, name: e.target.value }))}
            />
            <select
              value={newUser.role}
              onChange={(e) => setNewUser(prev => ({ ...prev, role: e.target.value }))}
            >
              <option value="subscriber">Gebruiker</option>
              <option value="admin">Beheerder</option>
            </select>
            <button type="submit" className="btn btn-primary">
              Aanmaken
            </button>
          </div>
        </form>

        <div className="admin-users-table-wrap">
          {usersLoading ? (
            <p className="loading">Gebruikers laden...</p>
          ) : (
            <table className="admin-users-table">
              <thead>
                <tr>
                  <th>E-mail</th>
                  <th>Naam</th>
                  <th>Werk-URL</th>
                  <th>Rol</th>
                  <th>Licentie</th>
                  <th>Beheerder</th>
                  <th>Actief</th>
                  <th>Wachtwoord</th>
                  <th>Inloggen als</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className={u.is_active === false ? 'user-inactive' : ''}>
                    <td>{u.email}</td>
                    <td>{u.name || '—'}</td>
                    <td>
                      {werkUrlEditId === u.id ? (
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                          <input
                            type="url"
                            placeholder="https://kantoor.nl"
                            value={werkUrlEditValue}
                            onChange={(e) => setWerkUrlEditValue(e.target.value)}
                            style={{ fontSize: 11, padding: '3px 6px', width: 140 }}
                            autoFocus
                          />
                          <button
                            type="button"
                            className="btn btn-primary btn-small"
                            style={{ fontSize: 10, padding: '2px 6px' }}
                            onClick={() => handleSaveWerkUrl(u.id)}
                          >
                            OK
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary btn-small"
                            style={{ fontSize: 10, padding: '2px 6px' }}
                            onClick={() => { setWerkUrlEditId(null); setWerkUrlEditValue(''); }}
                          >
                            X
                          </button>
                        </div>
                      ) : (
                        <div
                          style={{ cursor: 'pointer', fontSize: 11, color: u.werk_url ? '#374151' : '#9ca3af', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          title={u.werk_url || 'Klik om in te stellen'}
                          onClick={() => { setWerkUrlEditId(u.id); setWerkUrlEditValue(u.werk_url || ''); }}
                        >
                          {u.werk_url || '— instellen'}
                        </div>
                      )}
                    </td>
                    <td>
                      <span className={`badge badge-${u.role}`}>
                        {u.role === 'admin' ? 'Beheerder' : 'Gebruiker'}
                      </span>
                    </td>
                    <td>
                      {(() => {
                        const status = getLicenseStatus(u);
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <span className="badge" style={{ background: status.color, color: '#fff', fontSize: 11, padding: '2px 8px', borderRadius: 10, display: 'inline-block', width: 'fit-content' }}>
                              {status.label}
                            </span>
                            {u.license_requested_at && !u.license_active && (
                              <span className="badge" style={{ background: '#f59e0b', color: '#fff', fontSize: 10, padding: '2px 6px', borderRadius: 10, display: 'inline-block', width: 'fit-content', fontWeight: 700 }}>
                                Verzoek!
                              </span>
                            )}
                            {u.role !== 'admin' && (
                              <>
                                {u.license_key ? (
                                  <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginTop: 2 }}>
                                    <code style={{ fontSize: 10, background: '#f3f4f6', padding: '2px 6px', borderRadius: 4, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={u.license_key}>
                                      {u.license_key}
                                    </code>
                                    <button
                                      type="button"
                                      className="btn btn-secondary btn-small"
                                      style={{ fontSize: 10, padding: '1px 6px' }}
                                      onClick={() => {
                                        navigator.clipboard.writeText(u.license_key);
                                        setSuccess('Licentiecode gekopieerd!');
                                        setTimeout(() => setSuccess(null), 2000);
                                      }}
                                    >
                                      Kopieer
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    type="button"
                                    className="btn btn-primary btn-small"
                                    style={{ fontSize: 10, padding: '2px 8px', marginTop: 2 }}
                                    disabled={licenseGenerating === u.id}
                                    onClick={() => handleGenerateLicense(u.id)}
                                  >
                                    {licenseGenerating === u.id ? '...' : 'Genereer code'}
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td>
                      <label className="toggle-label">
                        <input
                          type="checkbox"
                          checked={u.role === 'admin'}
                          onChange={() => handleToggleAdmin(u)}
                          disabled={u.is_fallback_admin}
                        />
                        <span className="toggle-slider" />
                      </label>
                    </td>
                    <td>
                      <label className="toggle-label">
                        <input
                          type="checkbox"
                          checked={u.is_active !== false}
                          onChange={() => handleToggleActive(u)}
                          disabled={u.is_fallback_admin}
                        />
                        <span className="toggle-slider" />
                      </label>
                    </td>
                    <td>
                      {u.is_fallback_admin ? (
                        <span className="text-muted" style={{ fontSize: 12 }} title="Wachtwoord wijzigen in Applicatie-instellingen">In Instellingen</span>
                      ) : passwordEditId === u.id ? (
                        <div className="password-edit-row">
                          <input
                            type="password"
                            placeholder="Nieuw wachtwoord (min. 6)"
                            value={passwordEditValue}
                            onChange={(e) => setPasswordEditValue(e.target.value)}
                            className="password-edit-input"
                            autoFocus
                          />
                          <button
                            type="button"
                            className="btn btn-primary btn-small"
                            onClick={() => handleSetPassword(u.id)}
                            disabled={passwordSaving || passwordEditValue.length < 6}
                          >
                            {passwordSaving ? '...' : 'Opslaan'}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary btn-small"
                            onClick={() => { setPasswordEditId(null); setPasswordEditValue(''); setError(null); }}
                          >
                            Annuleren
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-secondary btn-small"
                          onClick={() => { setPasswordEditId(u.id); setPasswordEditValue(''); setError(null); }}
                        >
                          Wijzigen
                        </button>
                      )}
                    </td>
                    <td>
                      {u.is_fallback_admin ? (
                        <span className="text-muted" style={{ fontSize: 12 }}>—</span>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-secondary btn-small"
                          onClick={() => handleLoginAs(u)}
                          title="Open de app als deze gebruiker"
                        >
                          Inloggen als
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!usersLoading && users.length === 0 && (
            <p className="empty-state">Geen gebruikers in de database. Maak er een aan of log in met de fallback-admin uit Applicatie Instellingen.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
